import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PDFUploadModal } from "@/components/PDFUploadModal";
import { pdfAPI } from "@/lib/api";
import { Upload, FileText, CheckCircle2, Eye } from "lucide-react";
import { translateStrengthName } from "@/lib/strength-i18n";

interface UploadReportSectionProps {
  onDataExtracted?: (data: any) => void;
}

export default function UploadReportSection({ onDataExtracted }: UploadReportSectionProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [latestReportId, setLatestReportId] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const loadLatestReport = async () => {
    try {
      const response = await pdfAPI.getHistory(1, 1);
      setLatestReportId(response.data?.[0]?.id || null);
    } catch (error) {
      setLatestReportId(null);
    }
  };

  useEffect(() => {
    loadLatestReport();
  }, []);

  const handleViewLatestReport = async () => {
    if (!latestReportId) return;

    const response = await pdfAPI.getReport(latestReportId);
    if (response.success && response.data) {
      setSelectedReport(response.data);
      setIsDetailOpen(true);
    }
  };

  return (
    <>
      <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 text-start sm:p-6" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          <div className="w-fit rounded-lg bg-primary/10 p-3 sm:p-4">
            <Upload className="w-8 h-8 text-primary" />
          </div>
          
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold mb-2 sm:text-2xl">
              {t('uploadSection.title')}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t('uploadSection.description')}
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>{t('uploadSection.feature1')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>{t('uploadSection.feature2')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span>{t('uploadSection.feature3')}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                size="lg"
                onClick={() => setPdfModalOpen(true)}
                className="w-full shadow-glow gap-2 sm:w-auto"
              >
                <FileText className="w-5 h-5" />
                {t('uploadSection.uploadButton')}
              </Button>
              {latestReportId && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleViewLatestReport}
                  className="w-full gap-2 sm:w-auto"
                >
                  <Eye className="w-5 h-5" />
                  {t("pdfReports.extractionTitle")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <PDFUploadModal
        open={pdfModalOpen}
        onOpenChange={setPdfModalOpen}
        onDataExtracted={(data) => {
          onDataExtracted?.(data);
          loadLatestReport();
        }}
      />

      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <DialogHeader>
            <DialogTitle>{t("pdfReports.extractionTitle")}</DialogTitle>
            <DialogDescription>
              {selectedReport?.targetUser?.name || selectedReport?.username}
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-5">
              <Card className="p-4 bg-muted/50 border-border">
                <p className="text-sm text-muted-foreground">{t("pdfReports.file")}</p>
                <p className="font-medium break-all">{selectedReport.fileName}</p>
              </Card>

              <div>
                <h3 className="font-semibold mb-3">{t("pdfReports.extractedStrengths")}</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(selectedReport.extractedData?.strengths || []).map((strength: any) => (
                    <div
                      key={`${strength.rank}-${strength.strengthName}`}
                      className="flex items-center gap-3 p-2 bg-muted/30 rounded border border-border/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold">{strength.rank}</span>
                      </div>
                      <p className="text-sm font-medium">
                        {translateStrengthName(strength.strengthName || strength.theme_name, t, i18n.language)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
